import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AssistantAction,
  AssistantAddTasksAction,
  AssistantAskOptionsAction,
  AssistantChatService,
  AssistantProposedTask,
  AssistantQuestionOption,
  AssistantUsage,
} from 'src/app/services/assistant-chat/assistant-chat.service';

/** Mensaje tal como se muestra en el panel. */
interface ChatBubble {
  role: 'user' | 'assistant';
  content: string;
  actions?: AssistantAction[];
  pending?: boolean;
  error?: boolean;
}

/** Sugerencias iniciales, tomadas de consultas reales de usuarios. */
const STARTERS: string[] = [
  'Agregar 20m² de piso de porcelanato 60x60 a mi computo.',
  '¿Qué materiales lleva un tabique de construcción en seco?',
  '¿Cuánto subió la mano de obra este año?',
  '¿Cuánto cuesta revocar una pared de 3 x 2,5 m?',
  'Tengo un problema con mi suscripción',
];

@Component({
  selector: 'app-chat-panel',
  templateUrl: './chat-panel.component.html',
  styleUrls: ['./chat-panel.component.sass'],
})
export class ChatPanelComponent implements OnInit {
  /** Controla la visibilidad desde el contenedor. */
  @Input() open = false;
  @Output() openChange = new EventEmitter<boolean>();

  /** Tareas confirmadas por el usuario para agregar al cómputo. */
  @Output() applyTasks = new EventEmitter<AssistantProposedTask[]>();

  @ViewChild('scrollArea') private scrollArea?: ElementRef<HTMLElement>;

  messages: ChatBubble[] = [];
  draft = '';
  loading = false;
  usage: AssistantUsage | null = null;
  limitReached = false;
  limitMessage = '';
  readonly starters = STARTERS;
  readonly maxLength = 1000;

  private sessionId: number | null = null;
  /** Acciones ya aplicadas, para no ofrecer dos veces el mismo botón. */
  private appliedActions = new Set<string>();
  /** Preguntas ya respondidas, por índice de burbuja. */
  private answeredQuestions = new Set<number>();

  constructor(private chatService: AssistantChatService) {}

  ngOnInit(): void {
    this.refreshUsage();
  }

  get canSend(): boolean {
    return !this.loading && !this.limitReached && this.draft.trim().length > 0;
  }

  get isEmpty(): boolean {
    return this.messages.length === 0;
  }

  close(): void {
    this.open = false;
    this.openChange.emit(false);
  }

  useStarter(text: string): void {
    this.draft = text;
    this.send();
  }

  send(): void {
    const message = this.draft.trim();
    if (!message || this.loading || this.limitReached) {
      return;
    }

    this.messages.push({ role: 'user', content: message });
    this.messages.push({ role: 'assistant', content: '', pending: true });
    this.draft = '';
    this.loading = true;
    this.scrollToBottom();

    this.chatService.ask(message, this.sessionId).subscribe({
      next: (res) => {
        this.sessionId = res.session_id;
        this.usage = res.usage;
        this.replacePending({
          role: 'assistant',
          content: res.reply,
          actions: res.actions?.length ? res.actions : undefined,
        });
        this.loading = false;
        this.scrollToBottom();
      },
      error: (err) => {
        this.loading = false;

        if (err?.status === 429) {
          const body = err.error ?? {};
          this.limitReached = true;
          this.usage = body.usage ?? this.usage;
          this.limitMessage =
            body.scope === 'monthly'
              ? 'Llegaste al límite mensual de consultas.'
              : 'Llegaste al límite de consultas por hoy.';
          this.replacePending({
            role: 'assistant',
            content: this.limitMessage,
            error: true,
          });
          return;
        }

        this.replacePending({
          role: 'assistant',
          content:
            err?.error?.message ??
            'No pude responder en este momento. Probá de nuevo en un rato.',
          error: true,
        });
      },
    });
  }

  /** Narrowing para el template: propuesta de tareas. */
  asAddTasks(action: AssistantAction): AssistantAddTasksAction | null {
    return action.action === 'add_tasks' ? action : null;
  }

  /** Narrowing para el template: repregunta con opciones. */
  asAskOptions(action: AssistantAction): AssistantAskOptionsAction | null {
    return action.action === 'ask_options' ? action : null;
  }

  /**
   * El usuario eligió una opción: se manda como si la hubiera escrito.
   *
   * Sólo se responde a la última pregunta, para que no pueda contestar una
   * de varios turnos atrás y descolocar la conversación.
   */
  chooseOption(bubbleIndex: number, option: AssistantQuestionOption): void {
    if (this.loading || !this.isLastBubble(bubbleIndex)) {
      return;
    }

    this.answeredQuestions.add(bubbleIndex);
    this.draft = option.value;
    this.send();
  }

  /** Una pregunta ya contestada deja de ofrecer sus botones. */
  isAnswered(bubbleIndex: number): boolean {
    return this.answeredQuestions.has(bubbleIndex) || !this.isLastBubble(bubbleIndex);
  }

  /** El usuario confirma la propuesta: recién acá se toca el cómputo. */
  confirmAction(bubbleIndex: number, action: AssistantAddTasksAction): void {
    const key = this.actionKey(bubbleIndex, action);
    if (this.appliedActions.has(key)) {
      return;
    }

    this.appliedActions.add(key);
    this.applyTasks.emit(action.tasks);
  }

  isApplied(bubbleIndex: number, action: AssistantAddTasksAction): boolean {
    return this.appliedActions.has(this.actionKey(bubbleIndex, action));
  }

  trackByIndex(index: number): number {
    return index;
  }

  /** Identifica una propuesta concreta dentro de la conversación. */
  private actionKey(bubbleIndex: number, action: AssistantAddTasksAction): string {
    return `${bubbleIndex}:${action.tasks.map((t) => t.task_id).join(',')}`;
  }

  private isLastBubble(index: number): boolean {
    return index === this.messages.length - 1;
  }

  private replacePending(bubble: ChatBubble): void {
    const index = this.messages.findIndex((m) => m.pending);
    if (index >= 0) {
      this.messages[index] = bubble;
    } else {
      this.messages.push(bubble);
    }
  }

  private refreshUsage(): void {
    this.chatService.getUsage().subscribe({
      next: (usage) => {
        this.usage = usage;
        this.limitReached = usage.remaining_today <= 0 || usage.remaining_month <= 0;
      },
      // Si falla (por ejemplo sin sesión), el backend valida igual al enviar.
      error: () => {},
    });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.scrollArea?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
